import { LinearInterpolator, TRANSITION_EVENTS } from "@deck.gl/core";
import type { BBox } from "geojson";
import { useAtom } from "jotai";
import { useCallback } from "react";
import { type MapState, mapStateFamily } from "../state";

export const useMap = (
    // FIXME: to avoid having to pass mapId everytime, accept either this input or a context (?)
    mapId: string,
    initialState?: Partial<MapState>,
) => {
    // Atom to hold map state here.
    const [mapState, setMapState] = useAtom(
        mapStateFamily({ mapId: mapId, initialState }),
    );

    // helper functions (navigate)
    const zoomIn = useCallback(() => {
        setMapState((ms) => ({
            ...ms,
            _viewState: {
                ...ms._viewState,
                zoom: ms._viewState.zoom + 1,
            },
        }));
    }, [setMapState]);

    const zoomOut = useCallback(() => {
        setMapState((ms) => ({
            ...ms,
            _viewState: {
                ...ms._viewState,
                zoom: Math.max(ms._viewState.zoom - 1, 1.5),
            },
        }));
    }, [setMapState]);

    const flyTo = useCallback(
        (
            latitude: number,
            longitude: number,
            zoom?: number,
            useInterpolator?: boolean,
        ) => {
            setMapState((ms) => ({
                ...ms,
                _viewState: {
                    ...ms._viewState,
                    latitude,
                    longitude,
                    zoom: zoom || ms._viewState.zoom,
                    transitionInterpolator: useInterpolator
                        ? new LinearInterpolator()
                        : undefined,
                    transitionDuration: 300,
                    transitionInterruption: TRANSITION_EVENTS.BREAK,
                    onTransitionEnd: () => {
                        setMapState((i) => {
                            i._viewState.transitionInterpolator = undefined;
                            return i;
                        });
                    },
                },
            }));
        },
        [setMapState],
    );

    const flyToBBox = useCallback(
        (bbox: BBox) => {
            const viewport = mapState.debounced.viewport;
            if (!viewport) return;

            const [minX, minY, maxX, maxY] = bbox;
            const { latitude, longitude, zoom } = viewport.fitBounds(
                [
                    [minX, minY],
                    [maxX, maxY],
                ],
                { padding: 120 },
            );
            flyTo(latitude, longitude, zoom, true);
        },
        [mapState.debounced.viewport, flyTo],
    );

    return {
        ...mapState,
        setMapState,
        zoomIn,
        zoomOut,
        flyTo,
        flyToBBox,
    };
};
